import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";

// The Supabase client (~200 KB) is loaded on demand rather than bundled into
// the first page: gameplay never needs it, and sign-in is optional. The
// provider starts the fetch on mount, so a returning signed-in player still
// has their session restored within moments of the page appearing.
const loadSupabase = () => import("@/integrations/supabase/client").then((m) => m.supabase);

// ═══════════════════════════════════════════════════════════════
// AuthProvider — app-wide Cloud auth state.
// ═══════════════════════════════════════════════════════════════
//
// Wraps the whole app at the root of <App />. Listens to
// supabase.auth.onAuthStateChange so any Settings → Sign in / out
// action is reflected everywhere without prop drilling.
//
// Sign-in is *optional*. The game stays fully playable signed-out
// (all gameplay state still lives in localStorage). Signing in is
// surfaced from Settings → Account so a player can sync progress
// across devices and protect their purchases.

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // Keep callbacks stable across renders so the memoised context
  // value doesn't churn every time auth state ticks.
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let unsubscribe: (() => void) | null = null;

    loadSupabase()
      .then((supabase) => {
        if (!mountedRef.current) return;
        // Listener registration BEFORE the initial getSession call, as
        // the auth docs recommend, so the very first state change is not
        // missed. It fires for SIGNED_IN / SIGNED_OUT / TOKEN_REFRESHED.
        const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
          if (!mountedRef.current) return;
          setSession(nextSession);
          setUser(nextSession?.user ?? null);
        });
        unsubscribe = () => sub.subscription.unsubscribe();

        // Initial hydration. getSession is fine here because subsequent
        // trust-sensitive reads can call getUser() explicitly.
        return supabase.auth.getSession().then(({ data }) => {
          if (!mountedRef.current) return;
          setSession(data.session);
          setUser(data.session?.user ?? null);
          setLoading(false);
        });
      })
      .catch((e) => {
        // Offline or a stale deploy: stay signed out; the game is fully
        // playable without an account.
        if (import.meta.env.DEV) console.warn("[auth] could not load the client", e);
        if (mountedRef.current) setLoading(false);
      });

    return () => {
      mountedRef.current = false;
      unsubscribe?.();
    };
  }, []);

  const signInGoogle = useCallback(async () => {
    const { lovable } = await import("@/integrations/lovable");
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
    });
    if (result.error) {
      throw result.error instanceof Error ? result.error : new Error(String(result.error));
    }
    // If result.redirected, the browser will navigate away; otherwise
    // the listener above will pick up the new session.
  }, []);

  const signOut = useCallback(async () => {
    const supabase = await loadSupabase();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, session, loading, signInGoogle, signOut }),
    [user, session, loading, signInGoogle, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
